import { createSlice, createSelector, PayloadAction, AnyAction } from "@reduxjs/toolkit";
import { ThunkAction } from "redux-thunk";
import type { AppState } from "@Store/index";
import { getCartLineKey, type CartItem } from "./cartItem";
import {
  resolveCmsProductForCartItem,
  getCmsUnitPriceForCartItem,
  getCmsCategorySlug,
} from "../services/cartValidation";
import { getCmsImageUrl } from "../services/unikitCms";

/** localStorage key for the persisted cart (unchanged from the source app). */
export const CART_STORAGE_KEY = "amber-unikit-cart";

export interface CartState {
  items: CartItem[];
  /** Slide-over cart panel visibility. */
  cartOpen: boolean;
  /** True once the cart has been rehydrated from localStorage on the client. */
  hydrated: boolean;
}

const initialState: CartState = {
  // Empty on the server and on first client render — hydrated after mount to avoid
  // SSR/hydration mismatches (see `useCartPersistence`).
  items: [],
  cartOpen: false,
  hydrated: false,
};

const cartSlice = createSlice({
  name: "essentialKitCart",
  initialState,
  reducers: {
    /** Add a line, or increment the matching line's quantity (matched by {@link getCartLineKey}). */
    addItem(state, action: PayloadAction<{ item: Omit<CartItem, "quantity">; silent?: boolean }>) {
      const { item, silent } = action.payload;
      const key = getCartLineKey(item);
      const existing = state.items.find((i) => getCartLineKey(i) === key);
      if (existing) {
        existing.quantity += 1;
        existing.id = existing.id ?? item.id;
        existing.slug = existing.slug ?? item.slug;
        existing.categorySlug = existing.categorySlug ?? item.categorySlug;
      } else {
        state.items.push({ ...item, quantity: 1 });
      }
      if (!silent) state.cartOpen = true;
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter((i) => getCartLineKey(i) !== action.payload);
    },
    updateQuantity(state, action: PayloadAction<{ lineKey: string; quantity: number }>) {
      const { lineKey, quantity } = action.payload;
      if (quantity <= 0) {
        state.items = state.items.filter((i) => getCartLineKey(i) !== lineKey);
        return;
      }
      const line = state.items.find((i) => getCartLineKey(i) === lineKey);
      if (line) line.quantity = quantity;
    },
    /** Patch a single line (used by the CMS "refresh price" flow). */
    updateCartLine(state, action: PayloadAction<{ lineKey: string; changes: Partial<CartItem> }>) {
      const line = state.items.find((i) => getCartLineKey(i) === action.payload.lineKey);
      if (line) Object.assign(line, action.payload.changes);
    },
    clearCart(state) {
      state.items = [];
      state.cartOpen = false;
    },
    setCartOpen(state, action: PayloadAction<boolean>) {
      state.cartOpen = action.payload;
    },
    /** Replace items with the persisted list and mark the cart hydrated. */
    hydrateFromStorage(state, action: PayloadAction<CartItem[]>) {
      state.items = action.payload;
      state.hydrated = true;
    },
    setHydrated(state, action: PayloadAction<boolean>) {
      state.hydrated = action.payload;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  updateCartLine,
  clearCart,
  setCartOpen,
  hydrateFromStorage,
  setHydrated,
} = cartSlice.actions;

type CartThunk<ReturnType = void> = ThunkAction<ReturnType, AppState, unknown, AnyAction>;

/** Client-only: read the persisted cart from localStorage and hydrate the store. */
export const hydrateCart = (): CartThunk => (dispatch) => {
  if (typeof window === "undefined") {
    dispatch(setHydrated(true));
    return;
  }
  let items: CartItem[] = [];
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    items = Array.isArray(parsed) ? parsed : [];
  } catch {
    items = [];
  }
  dispatch(hydrateFromStorage(items));
};

/**
 * Re-sync a single line's unit price, image, and identifiers from the CMS.
 * Pass a {@link getCartLineKey} value. Returns `{ ok, error? }`.
 */
export const refreshCartItemFromCms =
  (lineKey: string): CartThunk<Promise<{ ok: boolean; error?: string }>> =>
  async (dispatch, getState) => {
    const item = getState().essentialKitCart.items.find((i) => getCartLineKey(i) === lineKey);
    if (!item) return { ok: false, error: "Item not found" };
    if (!item.slug && !item.categorySlug) {
      return {
        ok: false,
        error: "Cannot refresh this line (open the product page and add it again).",
      };
    }
    const product = await resolveCmsProductForCartItem(item);
    if (!product) {
      return { ok: false, error: "Product not found in the catalogue." };
    }
    if (product.isAvailable === false) {
      return { ok: false, error: "This product is no longer available." };
    }
    dispatch(
      updateCartLine({
        lineKey,
        changes: {
          price: getCmsUnitPriceForCartItem(product, item),
          image: getCmsImageUrl(product),
          id: product.id ?? item.id,
          slug: product.slug ?? item.slug,
          categorySlug: getCmsCategorySlug(product) ?? item.categorySlug,
        },
      }),
    );
    return { ok: true };
  };

/* --- Selectors --- */

export const selectCartItems = (state: AppState): CartItem[] => state.essentialKitCart.items;
export const selectCartOpen = (state: AppState): boolean => state.essentialKitCart.cartOpen;
export const selectCartHydrated = (state: AppState): boolean => state.essentialKitCart.hydrated;
/** Number of distinct cart lines (not the sum of quantities), matching the source. */
export const selectCartCount = (state: AppState): number => state.essentialKitCart.items.length;
export const selectCartSubtotal = createSelector(selectCartItems, (items) =>
  items.reduce((sum, i) => sum + i.price * i.quantity, 0),
);

export default cartSlice.reducer;
